import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import type { GroupBy } from '../../pages/property-grouping';

interface GroupedListCountLineProps {
  /** The active grouping dimension (drives the pluralized "N cidades" copy). */
  by: Exclude<GroupBy, 'none'>;
  /** The total property count across all groups. */
  totalCount: number;
  /** The number of groups. */
  groupCount: number;
  /** Expands every group. */
  onExpandAll: () => void;
  /** Collapses every group. */
  onCollapseAll: () => void;
}

/**
 * The count line above the grouped table: "N imóveis encontrados · N cidades",
 * followed by the Expandir todos / Recolher todos links. The dimension noun
 * ("cidades", "bairros", …) is pluralized per the active dimension.
 *
 * @param props - The counts, dimension, and expand/collapse handlers.
 * @returns The count-line element.
 */
export const GroupedListCountLine: React.FC<GroupedListCountLineProps> = ({
  by,
  totalCount,
  groupCount,
  onExpandAll,
  onCollapseAll,
}) => {
  const { t } = useTranslation('features/properties');
  const groupsLabel = t(`list.grouping.groupCount.${by}`, { count: groupCount });

  const linkStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: 'var(--seed-base)',
    fontSize: 'var(--text-meta)',
    fontWeight: 'var(--font-semibold)' as unknown as number,
    cursor: 'pointer',
    fontFamily: 'var(--font-family)',
    padding: 0,
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 'var(--space-2)',
        padding: 'var(--space-1) var(--space-1) var(--space-2)',
      }}
    >
      <Text as="span" style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-meta)', fontVariantNumeric: 'tabular-nums' }}>
        {t('list.resultsCountGrouped', { count: totalCount, groups: groupsLabel })}
      </Text>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <UnstyledButton type="button" data-testid="expand-all" onClick={onExpandAll} style={linkStyle}>
          {t('list.grouping.expandAll')}
        </UnstyledButton>
        <Text component="span" aria-hidden="true" style={{ color: 'var(--text-tertiary)' }}>·</Text>
        <UnstyledButton type="button" data-testid="collapse-all" onClick={onCollapseAll} style={linkStyle}>
          {t('list.grouping.collapseAll')}
        </UnstyledButton>
      </div>
    </div>
  );
};
